import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import PolicyPage, { PolicyContactBox } from "./PolicyPage";

const mandatoryRights = (
  <p>
    Nothing on this page limits rights that cannot lawfully be excluded or
    restricted in the customer’s location.
  </p>
);

export function OrdersPolicy() {
  return (
    <PolicyPage
      activePath="/orders"
      title="Orders & cancellation"
      intro="When an order becomes binding, how changes are handled and what happens when stock or pricing is wrong."
      sections={[
        {
          id: "orders-placing",
          shortTitle: "Placing an order",
          title: "Placing an order",
          content: (
            <>
              <p>
                Items in a basket are not reserved. By submitting checkout, you offer
                to buy the listed products at the total shown and confirm that the
                order and contact information you supplied is accurate.
              </p>
              <p>
                An automated acknowledgement only confirms receipt of the request.
                A contract is formed when payment is accepted and we send an order
                confirmation, unless applicable law requires an earlier point.
              </p>
            </>
          ),
        },
        {
          id: "orders-review",
          shortTitle: "Review & acceptance",
          title: "Review, stock & acceptance",
          content: (
            <>
              <p>
                We may verify availability, destination, payment authorisation and
                obvious pricing errors before accepting an order. If an item is
                unavailable or an order cannot be accepted, we will explain the issue
                and reverse any captured payment for the affected item.
              </p>
              <p>
                We do not substitute products without consent. Products are sold only
                in reasonable household quantities unless a separate trade arrangement
                has been agreed.
              </p>
            </>
          ),
        },
        {
          id: "orders-changes",
          shortTitle: "Changes & cancellation",
          title: "Changes before dispatch",
          content: (
            <>
              <p>
                Email {siteConfig.contact.email} as soon as possible if an address,
                quantity or item is wrong. We will try to help, but cannot guarantee a
                change or cancellation after packing or payment processing has begun.
              </p>
              <p>
                If cancellation is no longer possible, the <Link to="/returns">returns
                policy</Link> explains the available next step. Statutory cooling-off
                rights remain unaffected where they apply.
              </p>
            </>
          ),
        },
        {
          id: "orders-cancelled-by-us",
          shortTitle: "Cancellation by us",
          title: "When we may cancel",
          content: (
            <>
              <p>
                We may cancel before dispatch where payment is not authorised, an
                address is unsupported, stock is unavailable, a material listing error
                occurred, fraud or misuse is reasonably suspected, or fulfilment would
                breach law or carrier restrictions.
              </p>
              <p>
                We will notify the customer and return amounts paid for cancelled items
                to the original payment method. We are not responsible for avoidable
                delays caused by incomplete or inaccurate order information.
              </p>
            </>
          ),
        },
        {
          id: "orders-contact",
          shortTitle: "Order help",
          title: "Order records & help",
          content: (
            <>
              <p>
                Keep the confirmation email and quote the order reference when asking
                for help. Never send full card numbers, security codes or account
                passwords by email.
              </p>
              {mandatoryRights}
              <PolicyContactBox label="Need to change an order?" />
            </>
          ),
        },
      ]}
    />
  );
}

export function PaymentsPolicy() {
  return (
    <PolicyPage
      activePath="/payments"
      title="Payments & security"
      intro="Accepted payment flow, authorisation, pricing and practical steps for a safer checkout."
      sections={[
        {
          id: "payments-methods",
          shortTitle: "Payment methods",
          title: "Payment methods & currency",
          content: (
            <>
              <p>
                The methods actually offered by the hosted checkout are the available
                methods for that order. Availability can vary by device, currency,
                issuing bank and destination. Prices are shown in {siteConfig.currency.code};
                a card issuer may apply its own conversion or international fee.
              </p>
              <p>
                We do not accept payment details by email. Any reference to a payment
                network describes compatibility only and does not imply sponsorship or
                endorsement.
              </p>
            </>
          ),
        },
        {
          id: "payments-authorisation",
          shortTitle: "Authorisation",
          title: "Authorisation, capture & failed payments",
          content: (
            <>
              <p>
                A payment provider and card issuer may run authentication and fraud
                checks. An authorisation can temporarily reserve funds before a charge
                completes. A failed or cancelled checkout does not create a confirmed
                order.
              </p>
              <p>
                A pending bank entry may take time to disappear even where payment was
                not captured. Contact the issuing bank first for timing, then email us
                with the order reference if the entry remains unexplained.
              </p>
            </>
          ),
        },
        {
          id: "payments-security",
          shortTitle: "Checkout security",
          title: "Hosted checkout & security",
          content: (
            <>
              <p>
                Checkout transfers the customer to a hosted payment page. Card details
                should be entered only on that secure provider page; this storefront
                does not ask for full card details in its own forms.
              </p>
              <ul>
                <li>Check the address bar and use a trusted device and network.</li>
                <li>Do not share one-time passcodes or card security codes.</li>
                <li>Close the page and contact us if the amount or currency is wrong.</li>
              </ul>
            </>
          ),
        },
        {
          id: "payments-prices",
          shortTitle: "Taxes & duties",
          title: "Prices, taxes & import charges",
          content: (
            <>
              <p>
                The checkout total shows product and delivery charges collected by us.
                Destination taxes, customs duties or carrier handling fees may be
                charged separately where they are not included in that total. The
                recipient is responsible for charges imposed by local authorities,
                subject to applicable law.
              </p>
              <p>
                Obvious pricing or calculation errors may be corrected before an order
                is accepted. We will request approval before proceeding at a corrected
                higher price.
              </p>
            </>
          ),
        },
        {
          id: "payments-refunds",
          shortTitle: "Refunds & disputes",
          title: "Refunds, chargebacks & payment help",
          content: (
            <>
              <p>
                Approved refunds are sent to the original payment method. Provider and
                bank processing times are outside our direct control. Please contact us
                first about an unrecognised charge or order problem so we can investigate;
                this does not remove any right to contact the payment provider or bank.
              </p>
              <div className="policy-inline-links">
                <Link to="/returns">Returns &amp; refunds</Link>
                <Link to="/privacy">Payment data privacy</Link>
              </div>
              <PolicyContactBox label="Payment question?" />
            </>
          ),
        },
      ]}
    />
  );
}

export function WarrantyPolicy() {
  return (
    <PolicyPage
      activePath="/warranty"
      title="Product care & warranty"
      intro="How to care for household objects and what to do when a product arrives damaged or develops a fault."
      sections={[
        {
          id: "warranty-inspection",
          shortTitle: "On arrival",
          title: "Inspecting an order",
          content: (
            <>
              <p>
                Check the outer parcel and products soon after delivery. If an item is
                missing, incorrect or damaged, keep the packaging and email photographs
                of the parcel, shipping label and item together with the order reference.
              </p>
              <p>
                Reporting promptly helps us investigate with the carrier, but a short
                reporting request does not override a longer mandatory legal right.
              </p>
            </>
          ),
        },
        {
          id: "warranty-care",
          shortTitle: "Care guidance",
          title: "Care, materials & safe use",
          content: (
            <>
              <p>
                Follow the product page, label and packaging instructions. Keep decorative
                objects away from heat, open flame and prolonged moisture unless expressly
                described as suitable. Use food-contact, candle and electrical items only
                for their stated purpose.
              </p>
              <p>
                Natural materials and handmade finishes may show small variations in
                colour, texture and dimensions. These characteristics are not necessarily
                faults, but they do not excuse a product that is unsafe or materially
                different from its description.
              </p>
            </>
          ),
        },
        {
          id: "warranty-coverage",
          shortTitle: "Fault coverage",
          title: "Faults & remedies",
          content: (
            <>
              <p>
                Where a product is faulty, unsafe, not as described or not of the quality
                required by applicable consumer law, contact us with evidence of purchase.
                Depending on the circumstances and law, the remedy may be repair,
                replacement, price reduction or refund.
              </p>
              <p>
                Change-of-mind requests follow the <Link to="/returns">returns policy</Link>.
                A voluntary statement on this page is additional to, and does not replace,
                statutory guarantees.
              </p>
            </>
          ),
        },
        {
          id: "warranty-exclusions",
          shortTitle: "What is not covered",
          title: "Damage outside fault coverage",
          content: (
            <>
              <p>
                Remedies may not cover fair wear, accidental damage after delivery,
                misuse, failure to follow care instructions, unauthorised alteration,
                commercial use of a domestic product or expected ageing of natural
                materials. Each request is assessed on its facts and applicable law.
              </p>
              {mandatoryRights}
              <PolicyContactBox label="Report a product problem" />
            </>
          ),
        },
      ]}
    />
  );
}

export function PromotionsPolicy() {
  return (
    <PolicyPage
      activePath="/promotions"
      title="Promotions & gift cards"
      intro="General rules for discount codes, offers, store credit and gift cards when they are made available."
      sections={[
        {
          id: "promotions-codes",
          shortTitle: "Discount codes",
          title: "Using a discount code",
          content: (
            <>
              <p>
                A code must be entered before checkout is completed and cannot normally
                be added retrospectively. Unless the offer states otherwise, codes are
                single-use, non-transferable, not redeemable for cash and cannot be
                combined with another promotion.
              </p>
              <p>
                Product, order-value, destination and date restrictions shown with an
                offer form part of its terms. Shipping, taxes and duties are excluded
                from a minimum spend unless expressly stated.
              </p>
            </>
          ),
        },
        {
          id: "promotions-availability",
          shortTitle: "Availability",
          title: "Availability, errors & fair use",
          content: (
            <>
              <p>
                Promotions may end at the published time or when allocated stock is
                exhausted. We may refuse or cancel misuse involving automation, duplicate
                accounts, resale quantities, altered codes or a clear technical error.
              </p>
              <p>
                If we cancel an accepted paid order, amounts collected for that order will
                be returned. These rules do not limit mandatory consumer rights.
              </p>
            </>
          ),
        },
        {
          id: "promotions-gift-cards",
          shortTitle: "Gift cards",
          title: "Gift cards & store credit",
          content: (
            <>
              <p>
                Gift cards or store credit apply only if they are offered by this store.
                Their value, validity, eligible currency and any lawful expiry are stated
                at issue. They cannot be used to buy another gift card unless expressly
                allowed.
              </p>
              <p>
                Treat a digital code like cash. Contact us promptly if a code is lost or
                disclosed; replacement is not guaranteed once value has been redeemed.
                Local law may require different expiry or refund treatment.
              </p>
            </>
          ),
        },
        {
          id: "promotions-returns",
          shortTitle: "Returns",
          title: "Returns from promotional orders",
          content: (
            <>
              <p>
                A refund reflects the amount actually paid after discount. If returning
                part of a bundle or threshold promotion makes the retained order
                ineligible, any adjustment will be explained and applied only where the
                original offer terms and law permit it.
              </p>
              <PolicyContactBox label="Question about an offer?" />
            </>
          ),
        },
      ]}
    />
  );
}

export function PrivacyChoicesPolicy() {
  return (
    <PolicyPage
      activePath="/privacy-choices"
      eyebrow="Privacy"
      title="Privacy choices"
      intro="A practical route for access, correction, deletion, objection and other data-rights requests."
      sections={[
        {
          id: "choices-available",
          shortTitle: "Available choices",
          title: "Your privacy choices",
          content: (
            <>
              <p>
                Depending on where you live and the reason information is processed,
                you may ask to access, correct, delete or receive a copy of personal
                information, restrict or object to processing, or withdraw consent.
              </p>
              <p>
                You can also unsubscribe using the link in a marketing email. Service
                messages about an order are not marketing and may still be sent when
                needed to fulfil the transaction.
              </p>
            </>
          ),
        },
        {
          id: "choices-browser",
          shortTitle: "Browser controls",
          title: "Cart, wishlist & cookie controls",
          content: (
            <>
              <p>
                Cart and wishlist selections are stored in the browser. Remove items in
                the interface or clear this site’s storage in browser settings to erase
                those local records. Blocking essential storage may prevent those
                features from persisting.
              </p>
              <p>
                This storefront does not currently state that it sells personal
                information or uses cross-context behavioural advertising. If those
                practices change, required opt-out controls will be provided before use.
              </p>
            </>
          ),
        },
        {
          id: "choices-request",
          shortTitle: "Make a request",
          title: "How to make a request",
          content: (
            <>
              <p>
                Email {siteConfig.contact.email} with “Privacy request” in the subject,
                the right you wish to exercise, your country or region and enough detail
                to locate the relevant interaction. Do not send identity documents unless
                requested through a secure method.
              </p>
              <p>
                We may ask for proportionate verification and may retain a limited record
                of the request where required for security, compliance or dispute handling.
              </p>
            </>
          ),
        },
        {
          id: "choices-response",
          shortTitle: "Response & appeal",
          title: "Response, authorised agents & appeals",
          content: (
            <>
              <p>
                Requests are handled within the time required by applicable law. An
                authorised agent may need to show authority and the customer may need to
                verify directly. If a request is refused or limited, we will explain why
                where the law permits and describe any available appeal route.
              </p>
              <p>
                You may also contact the competent privacy regulator. We will not
                discriminate against a person for exercising a privacy right.
              </p>
              <PolicyContactBox label="Submit a privacy request" linkLabel="Email privacy support" />
            </>
          ),
        },
      ]}
    />
  );
}

export function ComplaintsPolicy() {
  return (
    <PolicyPage
      activePath="/complaints"
      title="Complaints"
      intro="How to raise a concern about an order, product, service or policy and what information helps us resolve it."
      sections={[
        {
          id: "complaints-contact",
          shortTitle: "Raise a complaint",
          title: "How to raise a complaint",
          content: (
            <>
              <p>
                Email {siteConfig.contact.email} with “Complaint” and the order reference,
                a concise description of the issue, the outcome you are seeking and any
                useful photographs or correspondence. Do not include full payment-card
                details or passwords.
              </p>
              <p>
                Accessibility assistance or another reasonable communication method can
                be requested in the same message.
              </p>
            </>
          ),
        },
        {
          id: "complaints-process",
          shortTitle: "Review process",
          title: "Acknowledgement & review",
          content: (
            <>
              <p>
                We aim to acknowledge a complaint within five business days. We will
                review relevant order records, product information and carrier or payment
                evidence, then provide a reasoned response or an update if more time is
                required.
              </p>
              <p>
                Complex or cross-border matters can take longer. We may request additional
                information that is necessary and proportionate to investigate.
              </p>
            </>
          ),
        },
        {
          id: "complaints-outcomes",
          shortTitle: "Possible outcomes",
          title: "Outcomes & escalation",
          content: (
            <>
              <p>
                Depending on the facts, an outcome may include an explanation, correction,
                replacement, repair, refund or another remedy required by law. If you remain
                dissatisfied, reply and ask for a second review by someone not responsible
                for the original response where practicable.
              </p>
              <p>
                This process does not prevent a customer from contacting a consumer
                authority, privacy regulator, payment provider or court when entitled to
                do so. Any optional dispute-resolution body will be identified when it is
                available and applicable.
              </p>
              <PolicyContactBox label="Start a complaint" />
            </>
          ),
        },
      ]}
    />
  );
}

export function LegalNoticePolicy() {
  return (
    <PolicyPage
      activePath="/legal-notice"
      eyebrow="Legal"
      title="Legal notice"
      intro="Storefront identity, contact route and the status of information published on this website."
      note={{
        title: "Operator details",
        content: "ottireluna is the trading name displayed by this storefront. No company registration or tax number has been supplied in the project configuration; those details should be added before launch wherever law requires them.",
      }}
      sections={[
        {
          id: "legal-operator",
          shortTitle: "Store operator",
          title: "Store identity & contact",
          content: (
            <>
              <div className="policy-definition-list">
                <div><strong>Trading name</strong><p>{siteConfig.brandName}</p></div>
                <div><strong>Email</strong><p>{siteConfig.contact.email}</p></div>
                <div><strong>Contact address</strong><p>{siteConfig.contact.address}</p></div>
              </div>
              <p>
                General notices can be sent by email. Order enquiries should include the
                order reference but never full card details or passwords.
              </p>
            </>
          ),
        },
        {
          id: "legal-publication",
          shortTitle: "Website publication",
          title: "Website publication & availability",
          content: (
            <>
              <p>
                Product and policy information is published in good faith and may be
                corrected or updated. Temporary interruption, maintenance or a display
                error does not by itself create a right to buy at an obviously incorrect
                price.
              </p>
              <p>
                Order rights and obligations are governed by the terms presented at the
                time of purchase, together with mandatory consumer law.
              </p>
            </>
          ),
        },
        {
          id: "legal-notices",
          shortTitle: "Formal notices",
          title: "Formal notices & governing rules",
          content: (
            <>
              <p>
                No governing-law or exclusive-jurisdiction clause is asserted on this
                page because the legal operator and target markets have not been fully
                configured. Mandatory rules of a customer’s habitual residence may apply
                regardless of any future contractual choice.
              </p>
              <p>
                A production launch should add the legal operator’s registered name,
                registration number, tax identifier and any required licensing or dispute
                platform information after professional review.
              </p>
              <PolicyContactBox label="Legal notice question?" />
            </>
          ),
        },
      ]}
    />
  );
}

export function IntellectualPropertyPolicy() {
  return (
    <PolicyPage
      activePath="/intellectual-property"
      eyebrow="Legal"
      title="Intellectual property"
      intro="Permitted use of storefront content and a clear route to report material that may affect third-party rights."
      sections={[
        {
          id: "ip-scope",
          shortTitle: "Protected material",
          title: "Names, content & product material",
          content: (
            <>
              <p>
                Text, photography, illustrations, graphics, interface elements, product
                names and other material may be protected by copyright, trade mark,
                design or other rights belonging to their respective owners. Publication
                does not transfer those rights to site visitors.
              </p>
              <p>
                Third-party names and payment marks are used only to identify compatible
                services. Their appearance does not imply ownership, sponsorship or
                endorsement by {siteConfig.brandName}.
              </p>
            </>
          ),
        },
        {
          id: "ip-permitted",
          shortTitle: "Permitted use",
          title: "Personal use & restrictions",
          content: (
            <>
              <p>
                Visitors may view and use the site for personal shopping and may retain
                an order or policy copy for their records. Except as allowed by law or
                written permission, content must not be reproduced, republished, sold,
                adapted, scraped at scale, used to train a commercial model or presented
                in a way that suggests affiliation.
              </p>
              <p>
                Linking to a public page is permitted when the link is accurate, lawful
                and does not imply endorsement. We may ask for a misleading link to be
                corrected or removed.
              </p>
            </>
          ),
        },
        {
          id: "ip-report",
          shortTitle: "Report a concern",
          title: "Copyright, trade mark & design notices",
          content: (
            <>
              <p>
                To report potentially infringing material, email the exact page URL, a
                description of the protected work or right, the disputed material, your
                contact details, the basis of your authority and a good-faith statement
                that the use is not authorised.
              </p>
              <p>
                We may request reasonable supporting evidence, notify the person who
                supplied the material and remove or restrict access while reviewing the
                report. Knowingly false notices may cause harm and should not be submitted.
              </p>
              <PolicyContactBox label="Report an IP concern" linkLabel="Send a rights notice" />
            </>
          ),
        },
      ]}
    />
  );
}

export function AccessibilityPolicy() {
  return (
    <PolicyPage
      activePath="/accessibility"
      eyebrow="Information"
      title="Accessibility statement"
      intro="Our commitment to making product information and the shopping journey usable by as many people as possible."
      sections={[
        {
          id: "accessibility-commitment",
          shortTitle: "Commitment",
          title: "Our approach",
          content: (
            <>
              <p>
                We aim for clear structure, keyboard-operable controls, meaningful labels,
                readable contrast, useful image alternatives and layouts that adapt to
                zoom and smaller screens. Accessibility is considered during ongoing
                design and content work.
              </p>
              <p>
                This statement is a commitment to improvement, not a claim that every
                page is free of barriers or formally certified to a particular standard.
              </p>
            </>
          ),
        },
        {
          id: "accessibility-known",
          shortTitle: "Known limitations",
          title: "Known limitations & third parties",
          content: (
            <>
              <p>
                Some product imagery may not convey every visual detail through text,
                and a hosted payment experience is operated outside this storefront.
                Browser, assistive-technology and third-party combinations may behave
                differently.
              </p>
              <p>
                If a product detail, form or checkout step is inaccessible, contact us
                before ordering and we will try to provide the information or a reasonable
                alternative without requiring sensitive payment details by email.
              </p>
            </>
          ),
        },
        {
          id: "accessibility-feedback",
          shortTitle: "Feedback",
          title: "Request assistance or report a barrier",
          content: (
            <>
              <p>
                Email the page address, the task you were trying to complete, a short
                description of the barrier and, if comfortable, the browser or assistive
                technology used. We will use that information only to investigate and
                respond in line with the <Link to="/privacy">privacy notice</Link>.
              </p>
              <PolicyContactBox label="Accessibility support" linkLabel="Request assistance" />
            </>
          ),
        },
      ]}
    />
  );
}

export function DisclaimerPolicy() {
  return (
    <PolicyPage
      activePath="/disclaimer"
      eyebrow="Legal"
      title="Website disclaimer"
      intro="Important limits around general information, product appearance, external services and website availability."
      sections={[
        {
          id: "disclaimer-information",
          shortTitle: "General information",
          title: "Information, not professional advice",
          content: (
            <>
              <p>
                Editorial, styling, care and general website content is provided for
                ordinary shopping information. It is not legal, medical, safety,
                financial or other professional advice and should not replace product
                instructions or qualified guidance.
              </p>
              <p>
                Policies should be read together. If this disclaimer conflicts with a
                mandatory consumer right or an express product promise, that right or
                promise takes priority.
              </p>
            </>
          ),
        },
        {
          id: "disclaimer-products",
          shortTitle: "Product appearance",
          title: "Colour, scale & natural variation",
          content: (
            <>
              <p>
                Screens, lighting and photography can affect perceived colour and scale.
                Check stated dimensions and materials before ordering. Handmade and
                natural objects may vary slightly, but products must still match their
                material description and applicable quality requirements.
              </p>
              <p>
                Props shown in lifestyle photography are not included unless the product
                listing expressly says so.
              </p>
            </>
          ),
        },
        {
          id: "disclaimer-third-parties",
          shortTitle: "Third parties",
          title: "External links, carriers & payment services",
          content: (
            <>
              <p>
                External sites and hosted services have their own terms, privacy practices
                and availability. A link or compatibility reference is not an endorsement.
                We remain responsible for obligations that law assigns to the seller but
                cannot control every independent network, bank, customs or carrier event.
              </p>
            </>
          ),
        },
        {
          id: "disclaimer-liability",
          shortTitle: "Liability",
          title: "Website use & lawful limits",
          content: (
            <>
              <p>
                We take reasonable steps to keep the site available and accurate but do
                not promise uninterrupted access or that every technical issue will be
                corrected immediately. Users are responsible for maintaining suitable
                device security and backups.
              </p>
              <p>
                Nothing excludes liability for fraud, wilful misconduct, death or injury
                caused by negligence, breach of mandatory consumer law, or any other
                liability that cannot legally be limited.
              </p>
              <PolicyContactBox label="Question about this notice?" />
            </>
          ),
        },
      ]}
    />
  );
}
